// Library methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

// Utilities
import {
  FixationLossMethodOptions,
  FixationPointShapeOptions,
} from "../../utils/examReportsHelper";
import {
  DEFAULT_DECIBEL_VALUE_SCREENING,
  DEFAULT_DECIBEL_VALUE_FULL_120,
  DEFAULT_DECIBEL_VALUE_ESTERMAN,
  VISUAL_FIELD_PATH,
  examCardEnums,
  eyeTypes,
  DEFAULT_STARTING_ROW_INDEX,
  StimulusColors,
  positionTypes,
} from "../../enums";
import useClinic from "../useClinic";

export const useAddEditExamHelpers = () => {
  const { t } = useTranslation();
  const { clinicSettings, clinicLicensing } = useClinic();
  const { eyeLeft, eyeRight, eyeBoth } = eyeTypes;
  const { near, far } = positionTypes;

  // This is the default exam card order when adding exams
  const defaultExamOrder = useMemo(() => {
    let examValues = Object.values(examCardEnums);

    // remove synoptophore exam type if it's disabled on the clinic
    if (!clinicLicensing?.canUseSynoptophoreExams) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.SYNOPTOPHORE.type
      );
    }

    // remove RAPD exam type if it's disabled on the clinic
    if (!clinicLicensing?.canUseRAPDExams) {
      examValues = examValues.filter(
        (value) => value.type !== examCardEnums.RAPD.type
      );
    }

    return examValues.map((value) => {
      return {
        ...value,
        label: t(value.label),
        translatedType: t(value.translatedType),
      };
    });
  }, [
    clinicLicensing?.canUseSynoptophoreExams,
    clinicLicensing?.canUseRAPDExams,
    t,
  ]);

  const getDeselectedExamOrder = useCallback(
    ({ exam }) => {
      let filteredExamOrder = [...defaultExamOrder];

      // remove the selected exams from the default exam order
      exam.sectionTypeOrder.forEach((examType) => {
        filteredExamOrder = filteredExamOrder.filter(
          (examOrder) => examOrder.type !== examType
        );
      });

      return filteredExamOrder;
    },
    [defaultExamOrder]
  );

  const isVfOrFull120 = useCallback((selectedExam) => {
    const { examFieldType } = selectedExam;
    const isVfOrFull120 =
      examFieldType.includes(VISUAL_FIELD_PATH) ||
      examFieldType === examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath;

    return isVfOrFull120;
  }, []);

  const getSectionName = useCallback((selectedExam) => {
    const enumValues = Object.values(examCardEnums);
    const sectionName = enumValues.find(
      (value) => value.algorithmPath === selectedExam.examFieldType
    )?.sectionName;

    return sectionName;
  }, []);

  // Used for these exams:
  // Visual Field Screening, Visual Field Threshold, Full 120, Esterman
  const getVfEyeSection = useCallback(
    (eye, selectedExam) => {
      const {
        examFieldType,
        examType,
        decibelValue,
        fixationType,
        sectionOrder,
        visualGrid,
        stimulusSizeType,
        stimulusColor,
        optionalToggles,
      } = selectedExam;

      const getEyeOrder = () => {
        switch (eye) {
          case eyeRight:
            return sectionOrder?.right ?? 0;
          case eyeLeft:
            return sectionOrder?.left ?? 0;
          default:
            return 0;
        }
      };

      let vfEyeSection = {
        algorithm: examType,
        gridType: visualGrid,
        eye,
        order: getEyeOrder(),
        stimulusSizeType: stimulusSizeType ?? "III",
        fixationPointShapeType:
          fixationType.fixationPointShapeType ??
          FixationPointShapeOptions.Sphere,
        fixationLossMethodType:
          fixationType.fixationLossMethod ??
          FixationLossMethodOptions.EyeTracking,
        requiresTutorial: optionalToggles?.requiresTutorial ?? false,
        autoPause: optionalToggles?.autoPause ?? false,
        encouragement: optionalToggles?.encouragement ?? false,
        calibration: optionalToggles?.calibration ?? false,
        unlimitedTime: optionalToggles?.unlimitedTime ?? false,
      };

      // add stimulusColor to certain VF exams
      if (
        examFieldType === examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_THRESHOLDING.algorithmPath
      ) {
        vfEyeSection = { ...vfEyeSection, stimulusColor };
      }

      // add decibelValue to certain VF exams
      if (
        examFieldType === examCardEnums.VISUAL_FIELD_SCREENING.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_FULL_120.algorithmPath ||
        examFieldType === examCardEnums.VISUAL_FIELD_ESTERMAN.algorithmPath
      ) {
        vfEyeSection = { ...vfEyeSection, decibelValue };
      }

      return vfEyeSection;
    },
    [eyeRight, eyeLeft]
  );

  // Used for these exams:
  // Contrast Sensitivity, Color Vision, Visual Acuity, Synoptophore, RAPD
  const getEyeSection = useCallback(
    (eye, selectedExam) => {
      const {
        examType,
        examFieldType,
        sectionOrder,
        position,
        startingRowIndex,
        stereopsis,
        fusion,
        optionalToggles,
      } = selectedExam;

      const getEyeOrder = () => {
        switch (eye) {
          case eyeRight:
            return sectionOrder?.right ?? 0;
          case eyeLeft:
            return sectionOrder?.left ?? 0;
          default:
            return 0;
        }
      };

      let eyeSection = {
        algorithm: examType,
        eye,
        order: getEyeOrder(),
        requiresTutorial: optionalToggles?.requiresTutorial ?? false,
        calibration: optionalToggles?.calibration ?? false,
        unlimitedTime: optionalToggles?.unlimitedTime ?? false,
      };

      // add timeLimit to Color Vision exams
      if (examFieldType === examCardEnums.COLOR_VISION.algorithmPath) {
        eyeSection = {
          ...eyeSection,
          timeLimit: optionalToggles.unlimitedTime
            ? -1
            : clinicSettings?.colorVisionDefaultTimeLimit ?? 120,
        };
      }
      // add position and startingRowIndex to Visual Acuity exams
      if (examFieldType === examCardEnums.VISUAL_ACUITY.algorithmPath) {
        eyeSection = { ...eyeSection, position, startingRowIndex };
      }
      // add stereopsis, fusion, position to Synoptophore exams
      if (examFieldType === examCardEnums.SYNOPTOPHORE.algorithmPath) {
        eyeSection = { ...eyeSection, stereopsis, fusion, position };
      }

      return eyeSection;
    },
    [eyeRight, eyeLeft, clinicSettings?.colorVisionDefaultTimeLimit]
  );

  const autopopulateVfCard = useCallback(
    (exam, sectionName) => {
      const examSection = exam[sectionName]?.[0];

      const defaultVfCard = {
        examType: examSection?.algorithm,
        visualGrid: examSection?.gridType,
        stimulusSizeType: examSection?.stimulusSizeType,
        eyeCheckboxes: {
          right: Boolean(
            exam[sectionName]?.find((section) => section.eye === eyeRight)
          ),
          left: Boolean(
            exam[sectionName]?.find((section) => section.eye === eyeLeft)
          ),
        },
        sectionOrder: {
          right:
            exam[sectionName]?.find((section) => section.eye === eyeRight)
              ?.order ?? 0,
          left:
            exam[sectionName]?.find((section) => section.eye === eyeLeft)
              ?.order ?? 1,
        },
        fixationType: {
          fixationPointShapeType: examSection?.fixationPointShapeType,
          fixationLossMethod: examSection?.fixationLossMethodType,
        },
        optionalToggles: {
          autoPause: examSection?.autoPause ?? false,
          calibration: examSection?.calibration ?? false,
          encouragement: examSection?.encouragement ?? false,
          requiresTutorial: examSection?.requiresTutorial ?? false,
        },
      };

      return defaultVfCard;
    },
    [eyeLeft, eyeRight]
  );

  const autopopulateEstermanCard = useCallback(
    (exam, sectionName) => {
      const examSection = exam[sectionName]?.[0];

      const defaultEstermanCard = {
        examType: examSection?.algorithm,
        visualGrid: examSection?.gridType,
        binocular: examSection?.eye === eyeBoth,
        decibelValue: examSection?.decibelValue
          ? Number(examSection?.decibelValue)
          : DEFAULT_DECIBEL_VALUE_ESTERMAN,
        eyeCheckboxes: {
          right: true,
          left: true,
        },
        fixationType: {
          fixationPointShapeType: examSection?.fixationPointShapeType,
          fixationLossMethod: examSection?.fixationLossMethodType,
        },
        optionalToggles: {
          autoPause: examSection?.autoPause ?? false,
          calibration: examSection?.calibration ?? false,
          encouragement: examSection?.encouragement ?? false,
          requiresTutorial: examSection?.requiresTutorial ?? false,
        },
      };

      return defaultEstermanCard;
    },
    [eyeBoth]
  );

  const autopopulateNonVfCard = useCallback(
    (exam, sectionName) => {
      const examSection = exam[sectionName]?.[0];

      const defaultNonVfCard = {
        examType: examSection?.algorithm,
        binocular: exam[sectionName]?.every(
          (section) => section?.eye === eyeBoth
        ),
        eyeCheckboxes: {
          right: Boolean(
            exam[sectionName]?.find(
              (section) => section.eye === eyeRight || section.eye === eyeBoth
            )
          ),
          left: Boolean(
            exam[sectionName]?.find(
              (section) => section.eye === eyeLeft || section.eye === eyeBoth
            )
          ),
        },
        sectionOrder: {
          right:
            exam[sectionName]?.find((section) => section.eye === eyeRight)
              ?.order ?? 0,
          left:
            exam[sectionName]?.find((section) => section.eye === eyeLeft)
              ?.order ?? 1,
        },
      };

      return defaultNonVfCard;
    },
    [eyeBoth, eyeLeft, eyeRight]
  );

  const generateExamsToEdit = useCallback(
    ({ exam }) => {
      const examCardsToEdit = [];

      const enumValues = Object.values(examCardEnums);
      exam.sectionTypeOrder?.forEach((examName) => {
        // get the current section info
        const enumValue = enumValues.find((value) => value.type === examName);
        const sectionName = enumValue?.sectionName;
        const examFieldType = enumValue?.algorithmPath;
        const examSection = exam[sectionName]?.[0];

        switch (sectionName) {
          case examCardEnums.VISUAL_FIELD_THRESHOLDING.sectionName:
            const editVfThresholdCard = {
              ...autopopulateVfCard(exam, sectionName),
              examFieldType,
              stimulusColor: examSection?.stimulusColor ?? StimulusColors.WHITE,
            };
            examCardsToEdit.push(editVfThresholdCard);
            break;

          case examCardEnums.VISUAL_FIELD_SCREENING.sectionName:
            const editVfScreenCard = {
              ...autopopulateVfCard(exam, sectionName),
              examFieldType,
              stimulusColor: examSection?.stimulusColor ?? StimulusColors.WHITE,
              decibelValue: examSection?.decibelValue
                ? Number(examSection?.decibelValue)
                : DEFAULT_DECIBEL_VALUE_SCREENING,
            };
            examCardsToEdit.push(editVfScreenCard);
            break;

          case examCardEnums.VISUAL_FIELD_FULL_120.sectionName:
            const editFull120Card = {
              ...autopopulateVfCard(exam, sectionName),
              examFieldType,
              decibelValue: examSection?.decibelValue
                ? Number(examSection?.decibelValue)
                : DEFAULT_DECIBEL_VALUE_FULL_120,
            };
            examCardsToEdit.push(editFull120Card);
            break;

          case examCardEnums.VISUAL_FIELD_ESTERMAN.sectionName:
            const editEstermanCard = {
              ...autopopulateEstermanCard(exam, sectionName),
              examFieldType,
            };
            examCardsToEdit.push(editEstermanCard);
            break;

          case examCardEnums.CONTRAST_SENSITIVITY.sectionName:
            const editContrastSensitivityCard = {
              ...autopopulateNonVfCard(exam, sectionName),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
              },
            };
            examCardsToEdit.push(editContrastSensitivityCard);
            break;

          case examCardEnums.COLOR_VISION.sectionName:
            const editColorVisionCard = {
              ...autopopulateNonVfCard(exam, sectionName),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
                unlimitedTime: examSection?.timeLimit === -1 ?? false,
              },
            };
            examCardsToEdit.push(editColorVisionCard);
            break;

          case examCardEnums.VISUAL_ACUITY.sectionName:
            const editVisualAcuityCard = {
              ...autopopulateNonVfCard(exam, sectionName),
              examFieldType,
              startingRowIndex: isNaN(examSection?.startingRowIndex)
                ? DEFAULT_STARTING_ROW_INDEX
                : examSection?.startingRowIndex,
              position: examSection?.position ?? far,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
              },
            };
            examCardsToEdit.push(editVisualAcuityCard);
            break;

          case examCardEnums.SYNOPTOPHORE.sectionName:
            const editSynoptophoreCard = {
              ...autopopulateNonVfCard(exam, sectionName),
              examFieldType,
              fusion: examSection?.fusion,
              stereopsis: examSection?.stereopsis,
              position: examSection?.position ?? near,
              optionalToggles: {},
            };
            examCardsToEdit.push(editSynoptophoreCard);
            break;

          case examCardEnums.RAPD.sectionName:
            const editRAPDCard = {
              ...autopopulateNonVfCard(exam, sectionName),
              examFieldType,
              optionalToggles: {
                requiresTutorial: examSection?.requiresTutorial ?? false,
                calibration: examSection?.calibration ?? false,
              },
            };
            examCardsToEdit.push(editRAPDCard);
            break;

          default:
          // do nothing
        }
      });

      return examCardsToEdit;
    },
    [autopopulateVfCard, autopopulateEstermanCard, autopopulateNonVfCard]
  );

  return {
    defaultExamOrder,
    getDeselectedExamOrder,
    generateExamsToEdit,
    isVfOrFull120,
    getSectionName,
    getVfEyeSection,
    getEyeSection,
  };
};
